import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router";
import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { CaratzonLogo } from "~/components/marketing/svg/CaratzonLogo";
import { MarketingFooter } from "~/components/marketing/MarketingFooter";
import { LanguageSwitcher } from "~/components/marketing/LanguageSwitcher";
import { useT } from "~/i18n/context";

const navLinkStyle = { fontFamily: "var(--font-sans)" } as const;

function NavLink({ href, label, active, accent }: { href: string; label: string; active: boolean; accent?: string }) {
  return (
    <a
      href={href}
      className="issue-label transition-colors hover:text-foreground"
      style={{
        ...navLinkStyle,
        color: active ? (accent ?? "var(--teal-sapphire)") : undefined,
        opacity: active ? 1 : 0.4,
        borderBottom: active ? `1px solid ${accent ?? "var(--teal-sapphire)"}` : "none",
        paddingBottom: active ? "2px" : undefined,
      }}
    >
      {label}
    </a>
  );
}

function MoreDropdown({
  label,
  links,
  pathname,
}: {
  label: string;
  links: { href: string; label: string }[];
  pathname: string;
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const anyChildActive = links.some(({ href }) => pathname.startsWith(href));

  function handleEnter() {
    clearTimeout(closeTimer.current);
    setOpen(true);
  }
  function handleLeave() {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  }

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="issue-label transition-colors hover:text-foreground flex items-center gap-1"
        style={{
          ...navLinkStyle,
          color: anyChildActive ? "var(--teal-sapphire)" : undefined,
          opacity: anyChildActive ? 1 : 0.4,
          borderBottom: anyChildActive ? "1px solid var(--teal-sapphire)" : "none",
          paddingBottom: anyChildActive ? "2px" : undefined,
        }}
      >
        {label}
        <ChevronDown
          className="transition-transform duration-150"
          style={{ width: 13, height: 13, transform: open ? "rotate(180deg)" : undefined }}
        />
      </button>

      <div
        className="absolute left-1/2 pt-2 z-50 transition-all duration-150"
        style={{
          transform: "translateX(-50%)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <div className="rounded-md border border-border bg-background shadow-lg py-1.5 min-w-[10rem]">
          {links.map(({ href, label: linkLabel }) => {
            const active = pathname.startsWith(href);
            return (
              <a
                key={href}
                href={href}
                className="block px-4 py-2 text-sm transition-colors hover:bg-accent"
                style={{
                  fontFamily: "var(--font-sans)",
                  letterSpacing: "0.02em",
                  color: active ? "var(--teal-sapphire)" : undefined,
                  opacity: active ? 1 : 0.55,
                }}
              >
                {linkLabel}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type MarketingAudience = "brand" | "designer" | "neutral";

function detectAudience(pathname: string): MarketingAudience {
  if (pathname.startsWith("/brands") || pathname.startsWith("/ai"))
    return "brand";
  if (pathname.startsWith("/designers") || pathname.startsWith("/inspire"))
    return "designer";
  return "neutral";
}

export function MarketingLayout({ children, transparentNav }: { children: ReactNode; transparentNav?: boolean }) {
  const { pathname } = useLocation();
  const { t } = useT();
  const n = t.nav as Record<string, string>;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);

  useEffect(() => {
    if (!transparentNav) return;
    const onScroll = () => setScrolledPastHero(window.scrollY > 100);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentNav]);

  const audience = detectAudience(pathname);

  type NavItem = { href: string; label: string; accent?: string };

  const brandPrimary: NavItem[] = [
    { href: "/brands", label: n.overview ?? "Overview" },
    { href: "/brands/how-it-works", label: n.howItWorks ?? "How It Works" },
    { href: "/brands/pricing", label: n.pricing },
    { href: "/brands/ai", label: n.caraAi ?? "Cara AI" },
  ];
  const brandMore = [
    { href: "/about", label: n.about },
    { href: "/contact", label: n.contact },
    { href: "/academy", label: n.academy },
    { href: "/newsroom", label: n.newsroom ?? "Newsroom" },
  ];
  const brandSwitch = { href: "/designers", label: n.switchToDesigners ?? "For Designers →", accent: "var(--primary)" };

  const designerPrimary: NavItem[] = [
    { href: "/designers", label: n.overview ?? "Overview" },
    { href: "/inspire", label: n.inspire },
    { href: "/designers/ai", label: n.caraAi ?? "Cara AI" },
  ];
  const designerMore = [
    { href: "/about", label: n.about },
    { href: "/contact", label: n.contact },
    { href: "/academy", label: n.academy },
    { href: "/newsroom", label: n.newsroom ?? "Newsroom" },
  ];
  const designerSwitch = { href: "/brands", label: n.switchToBrands ?? "For Brands →", accent: "var(--teal-sapphire)" };

  const neutralPrimary: NavItem[] = [
    { href: "/designers", label: n.forDesigners, accent: "var(--primary)" },
    { href: "/brands", label: n.forBrands, accent: "var(--teal-sapphire)" },
  ];
  const neutralMore = [
    { href: "/about", label: n.about },
    { href: "/contact", label: n.contact },
    { href: "/academy", label: n.academy },
    { href: "/newsroom", label: n.newsroom ?? "Newsroom" },
  ];

  const PRIMARY_LINKS = audience === "brand" ? brandPrimary : audience === "designer" ? designerPrimary : neutralPrimary;
  const MORE_LINKS = audience === "brand" ? brandMore : audience === "designer" ? designerMore : neutralMore;
  const SWITCH_LINK = audience === "brand" ? brandSwitch : audience === "designer" ? designerSwitch : null;

  const ALL_MOBILE_LINKS = [...PRIMARY_LINKS, ...MORE_LINKS];

  return (
    <div data-theme="client" className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* ── Masthead ── */}
      <header
        className="px-6 lg:px-12 pt-5 sm:pt-7 pb-0 relative z-10"
        style={transparentNav ? {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          background: scrolledPastHero ? "var(--background)" : "transparent",
          color: scrolledPastHero ? "var(--foreground)" : "oklch(0.92 0.005 90)",
          transition: "background 0.4s ease, color 0.4s ease",
        } : undefined}
      >
        <div className="flex items-center justify-between gap-4">
          {/* Wordmark */}
          <a href="/" className="flex items-center gap-2.5 sm:gap-3 leading-none shrink-0">
            <CaratzonLogo size={28} className="shrink-0 opacity-90 sm:hidden" />
            <CaratzonLogo size={36} className="shrink-0 opacity-90 hidden sm:block" />
            <div>
              <span
                className="masthead-wordmark text-foreground block"
                style={{
                  fontSize: "clamp(1.05rem, 1.8vw, 1.4rem)",
                  color: transparentNav && !scrolledPastHero ? "inherit" : undefined,
                }}
              >
                Caratzon
              </span>
              <span className="hidden sm:block issue-label mt-1" style={navLinkStyle}>
                {audience === "brand" ? n.sublineBrand ?? t.nav.subline : audience === "designer" ? n.sublineDesigner ?? t.nav.subline : t.nav.subline}
              </span>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 pb-1">
            {PRIMARY_LINKS.map(({ href, label, accent }) => (
              <NavLink key={href} href={href} label={label} active={pathname === href || (href !== "/brands" && href !== "/designers" && pathname.startsWith(href))} accent={accent} />
            ))}
            <div className="w-px h-3 bg-border" />
            <MoreDropdown
              label={n.more ?? "More"}
              links={MORE_LINKS}
              pathname={pathname}
            />
            {SWITCH_LINK && (
              <>
                <div className="w-px h-3 bg-border" />
                <a
                  href={SWITCH_LINK.href}
                  className="issue-label transition-colors hover:text-foreground"
                  style={{
                    ...navLinkStyle,
                    opacity: 0.35,
                    color: SWITCH_LINK.accent,
                  }}
                >
                  {SWITCH_LINK.label}
                </a>
              </>
            )}
            <div className="border-l border-border pl-5" style={{ paddingBottom: "1px" }}>
              <LanguageSwitcher />
            </div>
          </nav>

          {/* Right side: CTA + hamburger */}
          <div className="flex items-center gap-3">
            <a
              href="/sign-in"
              className="issue-label transition-colors shrink-0 hidden lg:block"
              style={{ ...navLinkStyle, color: "var(--teal-sapphire)", opacity: 0.85, paddingBottom: "1px" }}
            >
              {t.nav.accessPlatform}
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden flex flex-col justify-center items-center w-8 h-8 gap-[5px]"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              <span
                className="block w-5 h-[1.5px] bg-foreground transition-all duration-200 origin-center"
                style={mobileMenuOpen ? { transform: "translateY(3.25px) rotate(45deg)" } : {}}
              />
              <span
                className="block w-5 h-[1.5px] bg-foreground transition-all duration-200 origin-center"
                style={mobileMenuOpen ? { transform: "translateY(-3.25px) rotate(-45deg)" } : {}}
              />
            </button>
          </div>
        </div>

        {/* Mobile slide-down menu */}
        <div
          className="lg:hidden overflow-hidden transition-all duration-200 ease-out -mx-6"
          style={{
            maxHeight: mobileMenuOpen ? "600px" : "0px",
            opacity: mobileMenuOpen ? 1 : 0,
            background: "var(--background)",
            color: "var(--foreground)",
          }}
        >
          <nav className="px-6 pt-4 pb-2 space-y-0">
            {ALL_MOBILE_LINKS.map(({ href, label }, i) => {
              const active = pathname === href || (href !== "/brands" && href !== "/designers" && pathname.startsWith(href));
              const isPrimary = i < PRIMARY_LINKS.length;
              return (
                <a
                  key={href}
                  href={href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 transition-colors"
                  style={{
                    fontSize: isPrimary ? "0.92rem" : "0.84rem",
                    ...navLinkStyle,
                    letterSpacing: "0.02em",
                    fontWeight: isPrimary ? 500 : undefined,
                    color: active ? "var(--teal-sapphire)" : undefined,
                    opacity: active ? 1 : isPrimary ? 0.7 : 0.45,
                  }}
                >
                  {label}
                  {i === PRIMARY_LINKS.length - 1 ? (
                    <div className="rule mt-3" />
                  ) : null}
                </a>
              );
            })}
            {SWITCH_LINK && (
              <a
                href={SWITCH_LINK.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 transition-colors"
                style={{
                  fontSize: "0.78rem",
                  ...navLinkStyle,
                  letterSpacing: "0.02em",
                  color: SWITCH_LINK.accent,
                  opacity: 0.4,
                }}
              >
                {SWITCH_LINK.label}
              </a>
            )}
            <div className="rule mt-1" />
            <div className="flex items-center justify-between pt-3 pb-1">
              <a
                href="/sign-in"
                onClick={() => setMobileMenuOpen(false)}
                className="issue-label transition-colors"
                style={{ ...navLinkStyle, color: "var(--teal-sapphire)" }}
              >
                {t.nav.accessPlatform}
              </a>
              <LanguageSwitcher />
            </div>
          </nav>
        </div>

        {!transparentNav && <div className="rule mt-3 sm:mt-4" />}
      </header>

      {/* Page content */}
      <main>{children}</main>

      <MarketingFooter audience={audience} />
    </div>
  );
}
